import {STYLE_SETTER_OPTIONS} from '@js/modules/maps/mapConstants';
import {letBrowserHandleInput} from '@js/modules/utils';

// The map is usable once its style is parsed (sources and layers can be added); `load` also waits for
// every tile of the first screen. `load` stays as the safety net should `style.load` not fire.
// style.load fires inside MapLibre's parsing task: starting in a task of our own lets a tap in between.
export const whenMapUsable = (map, callback) => {
    let started = false;
    const startAfterInput = () => {
        const style = map.style;

        letBrowserHandleInput().then(() => {
            if (!map.style) {
                return;
            }

            // Style switched meanwhile: its successor is not parsed yet.
            if (map.style !== style) {
                map.once('style.load', startAfterInput);
                return;
            }

            callback();
        });
    };
    const start = () => {
        if (started) {
            return;
        }
        started = true;

        startAfterInput();
    };

    map.once('style.load', start);
    map.once('load', start);
};

const isDemLayer = (layer, style) => style.sources?.[layer.source]?.type === 'raster-dem';

// Hide the visible layers drawn from a raster-dem source (hillshade, colour relief): their tiles are the
// heaviest of the first screen. The source stays, so 3D terrain keeps working.
export const deferDemLayers = (style) => {
    const hiddenLayerIds = [];

    const layers = (style.layers || []).map((layer) => {
        if (!isDemLayer(layer, style) || layer.layout?.visibility === 'none') {
            return layer;
        }

        hiddenLayerIds.push(layer.id);

        return {...layer, layout: {...layer.layout, visibility: 'none'}};
    });

    return {style: {...style, layers}, hiddenLayerIds};
};

// One layer per task: each one starts heavy tile work, a tap must not wait behind all of them.
export const revealLayers = async (map, layerIds) => {
    const style = map.style;

    for (const [index, layerId] of layerIds.entries()) {
        if (index > 0) {
            // eslint-disable-next-line no-await-in-loop -- one layer per task is the point
            await letBrowserHandleInput();
        }

        // Map removed, or another style loaded meanwhile: its layers are not ours to show.
        if (!map.style || map.style !== style) {
            return;
        }

        if (map.getLayer(layerId)) {
            map.setLayoutProperty(layerId, 'visibility', 'visible', STYLE_SETTER_OPTIONS);
        }
    }
};

// One download per image url, registered under every id that uses it.
export const loadMarkerImages = (map, images) => {
    const byUrl = new Map();

    images.forEach((image) => {
        if (map.hasImage(image.id)) {
            return;
        }

        byUrl.set(image.url, [...(byUrl.get(image.url) || []), image.id]);
    });

    return Promise.all([...byUrl].map(([url, ids]) => map.loadImage(url)
        .then((imageElement) => {
            ids.forEach((id) => {
                if (map.style && !map.hasImage(id)) {
                    map.addImage(id, imageElement.data);
                }
            });
        })));
};
